import { getLength } from 'ol/sphere';
import { LineString, MultiPoint } from 'ol/geom';
import { distance } from 'ol/coordinate';
import { unByKey as olUnByKey } from 'ol/Observable';
import { createEditingStyle } from 'ol/style/Style';

import { formatLength } from 'cadenza/utils/format/format';
import type { Options } from 'cadenza/map/interactions/measure';
import { attachMeasureOverlayToFeature, Measure } from 'cadenza/map/interactions/measure';
import { TooltipOverlay } from 'cadenza/map/tooltip/tooltip-overlay';
import type { EventKey } from 'cadenza/utils/event-util';
import { on, unByKey } from 'cadenza/utils/event-util';
import { GeometryType } from 'cadenza/map/geom/geometry-type';
import { MapProperty } from 'cadenza/map/map-property';
import { assertNonNullable, assertType } from 'cadenza/utils/custom-error';

import type { Feature, MapBrowserEvent, Map } from 'ol';
import type { StyleFunction } from 'ol/style/Style';
import type { EventsKey } from 'ol/events';
import type { Coordinate } from 'ol/coordinate';
import type { DrawEvent } from 'ol/interaction/Draw';

// 12 is also the default snap-to-finish distance by ol/Draw.
const MIN_PIXEL_DISTANCE_TO_SHOW_LATEST_PARTIAL_TOOLTIP = 12;

export class MeasureDistance extends Measure {

  readonly #partialDistanceTooltips: TooltipOverlay[] = [];
  #latestFixedLineCoordInPixel?: Coordinate;
  #mapPointerMoveEventKey?: EventsKey;
  #mouseEnterLatestPartialTooltipEventKey?: EventKey;

  constructor (map: Map, options?: Options) {
    super(map, 'LineString', createMeasureDistanceStyle(map), options);
  }

  override onDeactivation () {
    super.onDeactivation();
    // clean up
    this.#latestFixedLineCoordInPixel = undefined;
    olUnByKey(this.#mapPointerMoveEventKey!);
    this.#mapPointerMoveEventKey = undefined;
    unByKey(this.#mouseEnterLatestPartialTooltipEventKey);
    this.#mouseEnterLatestPartialTooltipEventKey = undefined;
  }

  override onMeasureStart (event: DrawEvent) {
    super.onMeasureStart(event);

    this.#mapPointerMoveEventKey = this.map.on('pointermove', (mapEvent: MapBrowserEvent<MouseEvent>) => {
      const latestPartialDistanceTooltip = this.latestPartialDistanceTooltip;
      if (!latestPartialDistanceTooltip) {
        return;
      }
      const pointerLocation = mapEvent.pixel;
      // we don't show partial distance inside snapping distance.
      if (distance(pointerLocation, this.#latestFixedLineCoordInPixel!) < MIN_PIXEL_DISTANCE_TO_SHOW_LATEST_PARTIAL_TOOLTIP) {
        if (latestPartialDistanceTooltip.visible) {
          latestPartialDistanceTooltip.hide();
        }
      } else {
        if (!latestPartialDistanceTooltip.visible) {
          latestPartialDistanceTooltip.show();
        }
      }
    });
  }

  protected updateMeasure (measure: TooltipOverlay, geometry: LineString): void {
    let segmentCount = this.#getSegmentCountForLine(geometry);
    while (segmentCount > this.segmentCount) {
      // we don't show partial distance, since it's the same as the full distance.
      if (segmentCount === 1) {
        break;
      }
      this.#pushPartialDistance();
      this.#updateLatestPartialDistanceTooltip(geometry);
      if (segmentCount === this.segmentCount) {
        this.#latestFixedLineCoordInPixel = this.map.getPixelFromCoordinate(geometry.getCoordinates()[this.latestSegmentIndex]);
        // we hide new latest tooltip initially since it will be discarded on snapping.
        const latestPartialDistanceTooltip = this.latestPartialDistanceTooltip;
        latestPartialDistanceTooltip.hide();
        // we set the latest partial distance to opposite positioning to the measureTooltip
        if (latestPartialDistanceTooltip.positioning === this.measureTooltip!.positioning) {
          if (latestPartialDistanceTooltip.positioning === 'bottom-center') {
            latestPartialDistanceTooltip.positioning = 'top-center';
          } else {
            latestPartialDistanceTooltip.positioning = 'bottom-center';
          }
        }
        // we register mouse enter on the latest partial distance overlay to flip it as well as the measure tooltip
        // first unregister the last one, if any
        if (this.#mouseEnterLatestPartialTooltipEventKey) {
          unByKey(this.#mouseEnterLatestPartialTooltipEventKey);
        }
        this.#mouseEnterLatestPartialTooltipEventKey = on(
          latestPartialDistanceTooltip.element!,
          'mouseenter',
          () => {
            if (latestPartialDistanceTooltip.positioning === 'bottom-center') {
              latestPartialDistanceTooltip.positioning = 'top-center';
              this.measureTooltip!.positioning = 'bottom-center';
            } else {
              latestPartialDistanceTooltip.positioning = 'bottom-center';
              this.measureTooltip!.positioning = 'top-center';
            }
          });
      }
    }
    if (this.hasPartialDistance) {
      this.#updateLatestPartialDistanceTooltip(geometry);
    }
    measure.message = this.#getFormattedDistanceForLine(geometry);
    measure.position = geometry.getLastCoordinate();
  }

  override onMeasureEnd (event: DrawEvent) {
    super.onMeasureEnd(event);
    // remove the latest partial distance, it is on currently on endpoint
    const latestTooltip = this.latestPartialDistanceTooltip;
    latestTooltip.deactivate();
    this.removeMeasureOverlayFromMeasure(latestTooltip);
    // clean up
    this.#latestFixedLineCoordInPixel = undefined;
    olUnByKey(this.#mapPointerMoveEventKey!);
    this.#mapPointerMoveEventKey = undefined;
    unByKey(this.#mouseEnterLatestPartialTooltipEventKey);
    this.#mouseEnterLatestPartialTooltipEventKey = undefined;
    this.#partialDistanceTooltips.length = 0;
  }

  override onMeasureAbort () {
    super.onMeasureAbort();

    // clean up
    this.#latestFixedLineCoordInPixel = undefined;
    olUnByKey(this.#mapPointerMoveEventKey!);
    this.#mapPointerMoveEventKey = undefined;
    unByKey(this.#mouseEnterLatestPartialTooltipEventKey);
    this.#mouseEnterLatestPartialTooltipEventKey = undefined;
    this.#partialDistanceTooltips.length = 0;
  }

  #pushPartialDistance () {
    const overlay = new TooltipOverlay(this.map, {
      followMouse: false,
      showArrow: true,
      positioning: 'top-center',
      coloring: 'secondary'
    });
    overlay.activate();
    overlay.show();
    this.#partialDistanceTooltips.push(overlay);
    this.attachMeasureOverlayToMeasure(overlay);
  }

  #getFormattedDistanceForLine (line: LineString) {
    return getFormattedDistanceForLine(line, this.map, 'metric');
  }

  #updateLatestPartialDistanceTooltip (line: LineString) {
    const segmentCoordinates = line.getCoordinates().slice(this.latestSegmentIndex, this.latestSegmentIndex + 2);
    const segmentLine = new LineString(segmentCoordinates);
    const latestTooltip = this.latestPartialDistanceTooltip;
    segmentLine.getCoordinateAt(0.5, latestTooltip.position);
    latestTooltip.message = this.#getFormattedDistanceForLine(segmentLine);
  }

  #getSegmentCountForLine (line: LineString) {
    return line.getCoordinates().length - 1;
  }

  get hasPartialDistance (): boolean {
    return this.latestSegmentIndex >= 0;
  }

  get latestPartialDistanceTooltip (): TooltipOverlay {
    return this.#partialDistanceTooltips[this.latestSegmentIndex];
  }

  get latestSegmentIndex (): number {
    return this.segmentCount - 1;
  }

  get segmentCount (): number {
    return this.#partialDistanceTooltips.length;
  }

  get resettable () {
    return true;
  }

}

export function createMeasureDistanceStyle (map: Map) {
  const cursorStyle = createEditingStyle()[GeometryType.POINT][0];
  const lineStyle = map.get(MapProperty.DRAW_STYLES)['LineString'];
  const lineEndingStyle = lineStyle.clone();
  lineEndingStyle.setGeometry((geometryFeature: Feature) => {
    const line = geometryFeature.getGeometry();
    if (!line || line.getType() !== 'LineString') {
      return;
    }
    assertType<LineString>(line, line.getType() === 'LineString');
    return new MultiPoint([ line.getFirstCoordinate(), line.getLastCoordinate() ]);
  });
  return ((feature: Feature) => {
    const geometry = feature.getGeometry();
    if (!geometry) {
      return;
    }
    if (geometry.getType() === 'Point') {
      return cursorStyle;
    }
    return [ lineStyle, lineEndingStyle ];
  }) as StyleFunction;
}

function getFormattedDistanceForLine (line: LineString, map: Map, unit: 'metric' | 'imperial') {
  const projection = map.getView().getProjection();
  return formatLength(getLength(line, { projection }), false, false, unit === 'metric');
}

export function setupDistanceMeasureOnFeature (feature: Feature<LineString>, map: Map, unit: 'metric' | 'imperial') {
  const measure = feature.getGeometry();
  assertNonNullable(measure);
  const segmentCount = measure.getCoordinates().length - 1;
  for (let i = 0; i < segmentCount; i++) {
    const partialMeasure = new LineString(measure.getCoordinates().slice(i, i + 2));
    const measureText = getFormattedDistanceForLine(partialMeasure, map, unit);
    const measurePosition = partialMeasure.getCoordinateAt(0.5);
    const overlay = new TooltipOverlay(map, {
      followMouse: false,
      showArrow: true,
      positioning: 'top-center',
      coloring: 'secondary',
      message: measureText,
      position: measurePosition
    });
    overlay.activate();
    overlay.show();
    attachMeasureOverlayToFeature(feature, overlay);
  }

  const measureText = getFormattedDistanceForLine(measure, map, unit);
  const measurePosition = measure.getLastCoordinate();
  const overlay = new TooltipOverlay(map, {
    followMouse: false,
    showArrow: true,
    positioning: 'top-center',
    coloring: 'primary',
    message: measureText,
    position: measurePosition
  });
  overlay.activate();
  overlay.show();
  attachMeasureOverlayToFeature(feature, overlay);
}
